import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';

const APP_ID = process.env.REACT_APP_ID;
const ENV = process.env.REACT_APP_ENV;

// Update the URL configuration to use local URL in development
let url = `https://${APP_ID}.execute-api.us-east-2.amazonaws.com/${ENV}`;
// Uncomment for local development
// url = 'http://127.0.0.1:3001';

interface DashboardResponse {
  dashboards: Array<{
    embed_id: string;
    name: string;
    jwt: {
      success: number;
      jwt: string;
    };
  }>;
}

interface ReportResponse {
  reports: Array<{
    embed_id: string;
    name: string;
    jwt: {
      success: number;
      jwt: string;
    };
  }>;
}

interface HomePageProps {
  user: CognitoUser | null;
}

const HomePage: React.FC<HomePageProps> = ({ user }) => {
  const [dashboardSrcs, setDashboardSrcs] = useState<string[]>([]);
  const [dashboardNames, setDashboardNames] = useState<string[]>([]);
  const [reportSrcs, setReportSrcs] = useState<string[]>([]);
  const [reportNames, setReportNames] = useState<string[]>([]);
  const [combinedLabels, setCombinedLabels] = useState<string[]>([]);
  const [combinedSrcs, setCombinedSrcs] = useState<string[]>([]);
  const [combinedTabIndex, setCombinedTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const getToken = async (): Promise<string | null> => {
    if (!user) return null;
    return new Promise((resolve) => {
      user.getSession((err: Error | null, session: any) => {
        if (err) {
          console.error("Error getting session:", err);
          resolve(null);
          return;
        }
        const token = session?.getIdToken().getJwtToken() || null;
        resolve(token);
      });
    });
  };

  useEffect(() => {
    const fetchDashboards = async () => {
      const token = await getToken();
      if (!token) return;

      try {
        const response = await fetch(`${url}/getExploDashboardsForUser`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data: DashboardResponse = await response.json();
        if (response.status === 200) {
          const valid = data.dashboards.filter(d => d.jwt.success === 1);
          setDashboardSrcs(valid.map(d => `https://app.explo.co/iframe/${d.jwt.jwt}`));
          setDashboardNames(valid.map(d => d.name));
        }
      } catch (err) {
        console.error("Error fetching dashboards:", err);
      }
    };

    if (user) fetchDashboards();
  }, [user]);

  useEffect(() => {
    const fetchReports = async () => {
      const token = await getToken();
      if (!token) return;

      try {
        const response = await fetch(`${url}/getExploReportBuildersForUser`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data: ReportResponse = await response.json();
        if (response.status === 200) {
          const valid = data.reports.filter(r => r.jwt.success === 1);
          setReportSrcs(valid.map(r => `https://app.explo.co/report-builder/iframe/${r.jwt.jwt}`));
          setReportNames(valid.map(r => r.name));
        }
      } catch (err) {
        console.error("Error fetching reports:", err);
      }
    };

    if (user) fetchReports();
  }, [user]);

  useEffect(() => {
    if (dashboardSrcs.length || reportSrcs.length) {
      const dashboardLabels = dashboardNames.map(name => `Dashboard: ${name}`);
      const reportLabels = reportNames.map(name => `Report: ${name}`);
      setCombinedLabels([...dashboardLabels, ...reportLabels]);
      setCombinedSrcs([...dashboardSrcs, ...reportSrcs]);
    }
  }, [dashboardSrcs, reportSrcs, dashboardNames, reportNames]);

  const handleLogout = () => {
    setIsLoading(true);
    localStorage.removeItem('id_token');
    user?.signOut(() => window.location.reload());
    setIsLoading(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Elevate</Typography>
          <Button color="inherit" onClick={handleLogout}>Logout</Button>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, height: '100vh', overflow: 'auto', backgroundColor: '#f5f5f5', pt: 8 }}>
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 4 }}>
          <Grid container spacing={3}>
            <Grid size={16}>
              <Paper sx={{ p: 3, backgroundColor: '#00539B', color: 'white' }}>
                <Typography variant="h4" gutterBottom>Welcome back!</Typography>
                <Typography>Your dashboard is ready for you.</Typography>
              </Paper>
            </Grid>

            <Grid size={16}>
              <Card>
                <CardContent>
                  <Tabs
                    value={combinedTabIndex}
                    onChange={(e, newValue) => setCombinedTabIndex(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {combinedLabels.map((label, idx) => (
                      <Tab key={idx} label={label} />
                    ))}
                  </Tabs>

                  {combinedSrcs.length > 0 && (
                    <Box
                      sx={{
                        height: combinedLabels[combinedTabIndex].startsWith('Dashboard') ? '2300px' : '800px',
                        mt: 2,
                      }}
                    >
                      <iframe
                        src={combinedSrcs[combinedTabIndex]}
                        style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
                        title={combinedLabels[combinedTabIndex]}
                      />
                    </Box>
                  )}

                  {combinedSrcs.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                      <CircularProgress />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;